import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ShieldCheckIcon,
  LockKeyIcon,
  DatabaseIcon,
  UsersIcon,
  GlobeIcon,
  EyeSlashIcon,
  FileTextIcon,
  EnvelopeSimpleIcon,
} from "@phosphor-icons/react";

const LAST_UPDATED = "April 23, 2026";
const EFFECTIVE = "April 23, 2026";

function Section({ icon: Icon, n, title, children, testId }) {
  return (
    <section className="mt-12 scroll-mt-24" data-testid={testId} id={testId}>
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-2xl bg-[#1D78AB]/10 text-[#1D78AB] grid place-items-center flex-shrink-0">
          <Icon size={20} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono tracking-widest text-zinc-400 uppercase">
            Section {n}
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-0.5">
            {title}
          </h2>
        </div>
      </div>
      <div className="mt-4 prose prose-zinc max-w-none text-[15px] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="privacy-page">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f3c56] via-[#1D78AB] to-[#2a9dd5] text-white p-8 sm:p-10 shadow-xl">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest uppercase text-white/80">
              <ShieldCheckIcon size={14} weight="fill" />
              Bidorah Privacy Policy
            </div>
            <h1
              className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter"
              data-testid="privacy-title"
            >
              Your data, on the record.
            </h1>
            <p className="mt-4 text-white/90 text-[15px] leading-relaxed max-w-2xl">
              At <strong>Bidorah</strong>, privacy isn&rsquo;t a footnote &mdash;
              it&rsquo;s the architecture. This policy explains exactly what
              Bidorah collects, why it&rsquo;s collected, how it&rsquo;s
              protected, and the controls you have over it. No jargon, no
              fine-print gymnastics.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-xs text-white/80">
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase opacity-80">
                  Last updated
                </div>
                <div className="font-bold text-sm text-white">{LAST_UPDATED}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase opacity-80">
                  Effective
                </div>
                <div className="font-bold text-sm text-white">{EFFECTIVE}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase opacity-80">
                  Governs
                </div>
                <div className="font-bold text-sm text-white">
                  All Bidorah services
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TOC */}
        <nav
          className="mt-10 rounded-2xl border border-zinc-200 bg-zinc-50 p-5"
          data-testid="privacy-toc"
        >
          <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-3">
            On this page
          </p>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-zinc-700 list-decimal pl-5 marker:text-[#1D78AB] marker:font-bold">
            <li><a href="#sec-1" className="hover:text-[#1D78AB]">What this policy covers</a></li>
            <li><a href="#sec-2" className="hover:text-[#1D78AB]">Information Bidorah collects</a></li>
            <li><a href="#sec-3" className="hover:text-[#1D78AB]">How Bidorah uses your data</a></li>
            <li><a href="#sec-4" className="hover:text-[#1D78AB]">Wholesale verification &amp; TIN handling</a></li>
            <li><a href="#sec-5" className="hover:text-[#1D78AB]">Live streams, chat &amp; tips</a></li>
            <li><a href="#sec-6" className="hover:text-[#1D78AB]">Payments, fees &amp; financial data</a></li>
            <li><a href="#sec-7" className="hover:text-[#1D78AB]">Cookies, storage &amp; analytics</a></li>
            <li><a href="#sec-8" className="hover:text-[#1D78AB]">Sharing &amp; disclosure</a></li>
            <li><a href="#sec-9" className="hover:text-[#1D78AB]">Security &amp; retention</a></li>
            <li><a href="#sec-10" className="hover:text-[#1D78AB]">Your rights &amp; choices</a></li>
            <li><a href="#sec-11" className="hover:text-[#1D78AB]">Children&rsquo;s privacy</a></li>
            <li><a href="#sec-12" className="hover:text-[#1D78AB]">International users</a></li>
            <li><a href="#sec-13" className="hover:text-[#1D78AB]">Changes to this policy</a></li>
            <li><a href="#sec-14" className="hover:text-[#1D78AB]">Contact Bidorah</a></li>
          </ol>
        </nav>

        <Section n="1" title="What this policy covers" icon={FileTextIcon} testId="sec-1">
          <p>
            This Privacy Policy (the &ldquo;Policy&rdquo;) applies to all
            products, services, websites, mobile experiences, and live auction
            marketplaces operated under the <strong>Bidorah</strong> brand
            (collectively, the &ldquo;Service&rdquo;). It explains how Bidorah
            collects, uses, stores, shares, and protects personal information
            from visitors, registered buyers, sellers, wholesale accounts, and
            admins who interact with Bidorah.
          </p>
          <p>
            By creating a Bidorah account, applying to sell on Bidorah, or
            otherwise using the Service, you acknowledge the data practices
            described here. If you do not agree, do not use Bidorah.
          </p>
        </Section>

        <Section n="2" title="Information Bidorah collects" icon={DatabaseIcon} testId="sec-2">
          <p>Bidorah collects three categories of information:</p>
          <h3 className="font-display font-bold text-lg mt-5">
            2.1 Information you provide directly
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Account details: name, email, password (hashed), account type (retail buyer, retail seller, wholesale buyer, wholesale seller).</li>
            <li>Profile content: display picture, bio, stream titles, stream descriptions, chat messages, tips, and listings posted to Bidorah.</li>
            <li>Government-issued US photo ID (required for Bidorah&rsquo;s mandatory identity verification).</li>
            <li>For wholesale buyers: Registered Business Entity, Federal/State TIN (EIN), and Reseller&rsquo;s Permit documentation.</li>
            <li>Payment and payout credentials you provide to Bidorah&rsquo;s payment processors.</li>
          </ul>
          <h3 className="font-display font-bold text-lg mt-5">
            2.2 Information Bidorah collects automatically
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Device and browser data: IP address, user agent, OS, browser type, device identifiers, referrer URLs.</li>
            <li>Usage data: pages visited on Bidorah, features used, listings viewed, bids placed, tips sent, watch-time on live streams.</li>
            <li>Technical logs: timestamps, error traces, performance metrics, and WebRTC/LiveKit signalling metadata for live streaming.</li>
          </ul>
          <h3 className="font-display font-bold text-lg mt-5">
            2.3 Information from third parties
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Google OAuth profile when you choose &ldquo;Sign in with Google&rdquo; on Bidorah.</li>
            <li>Payment-status webhooks from Stripe and PayPal.</li>
            <li>Compliance data from identity-verification providers when Bidorah reviews government ID.</li>
            <li>Referral metadata when another Bidorah user refers you.</li>
          </ul>
          <h3 className="font-display font-bold text-lg mt-5">
            2.4 Mobile app permissions (Android &amp; iOS)
          </h3>
          <p>
            The Bidorah mobile apps ask for the following device permissions
            only when the matching feature is used. None of these run in the
            background:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Camera</strong> — when you upload your government photo ID for verification, list an item, or go live as a seller. The camera turns off as soon as you close the screen.</li>
            <li><strong>Microphone</strong> — only while you are an active broadcaster or co-host on a live Bidorah stream.</li>
            <li><strong>Photos &amp; media</strong> — only when you tap the gallery picker to attach a listing photo or profile picture.</li>
            <li><strong>Notifications</strong> — only after you opt in. Used for outbid alerts, auction-ending reminders, stream-starts, and order updates. You can revoke this any time in your device settings.</li>
            <li>Bidorah does <strong>not</strong> request precise GPS location, contacts, calendar, or SMS access.</li>
          </ul>
        </Section>

        <Section n="3" title="How Bidorah uses your data" icon={ShieldCheckIcon} testId="sec-3">
          <p>
            Bidorah uses the information it collects strictly to operate,
            protect, and improve the Service:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Authenticating your Bidorah account and securing the session.</li>
            <li>Processing your bids, purchases, tips, and payouts.</li>
            <li>Enforcing Bidorah&rsquo;s retail-vs-wholesale market segregation at every buy/bid/checkout call.</li>
            <li>Running Bidorah&rsquo;s compliance gate for wholesale buyers and Founder Seller applicants.</li>
            <li>Delivering real-time chat, presence, tips, and live-stream video.</li>
            <li>Fraud detection, abuse prevention, and account security.</li>
            <li>Building aggregate, non-identifying analytics to improve Bidorah&rsquo;s product.</li>
            <li>Sending transactional emails and notifications you have opted into.</li>
          </ul>
          <p>
            Bidorah does <strong>not</strong> sell your personal information to
            data brokers, and Bidorah does not use your information to train
            third-party advertising profiles.
          </p>
        </Section>

        <Section
          n="4"
          title="Wholesale verification & TIN handling"
          icon={LockKeyIcon}
          testId="sec-4"
        >
          <p>
            Wholesale buyers on Bidorah must submit a Reseller&rsquo;s Permit,
            Federal/State TIN, and registered business entity before they can
            bid or purchase in the wholesale marketplace. Bidorah treats this
            information as &ldquo;sensitive&rdquo; data:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>TIN values are stored encrypted at rest; only the last four digits are exposed in any Bidorah admin surface by default.</li>
            <li>Access to the full TIN is restricted to Bidorah&rsquo;s compliance operators and only for reviewing the original application.</li>
            <li>Uploaded permit documents are scoped to the submitting user&rsquo;s object-storage path; Bidorah never surfaces them publicly.</li>
            <li>Revocation or denial of wholesale status immediately blocks the buyer&rsquo;s ability to transact in the wholesale segment.</li>
          </ul>
        </Section>

        <Section n="5" title="Live streams, chat & tips" icon={UsersIcon} testId="sec-5">
          <p>
            Bidorah operates public live streams. When you join a stream as a
            viewer, your public display name, profile picture, and presence
            (joined / left) are broadcast to other viewers in the room.
            Bidorah admins are visually flagged on every stream with an
            &ldquo;ADMIN&rdquo; badge.
          </p>
          <p>
            Chat messages, bids, and tips you send on a Bidorah live stream are
            public to all other viewers of that stream and are retained in
            Bidorah&rsquo;s transcript history for moderation and audit. Tips
            processed through Stripe include metadata identifying the tipper
            and recipient seller; Bidorah does not expose tipper payment card
            details to the seller.
          </p>
        </Section>

        <Section
          n="6"
          title="Payments, fees & financial data"
          icon={LockKeyIcon}
          testId="sec-6"
        >
          <p>
            Payments on Bidorah are processed by regulated third-party
            providers &mdash; currently <strong>Stripe</strong> and{" "}
            <strong>PayPal</strong>. Bidorah never stores full card numbers or
            bank routing details on Bidorah&rsquo;s own servers; only
            tokenised references, last-four digits, and high-level transaction
            metadata (amount, currency, order ID, fee split) are retained for
            accounting, dispute resolution, and payout reconciliation.
          </p>
          <p>
            Bidorah maintains a platform-revenue ledger that records the
            fee split for every order, tip, and payout. That ledger is
            accessible only to Bidorah admins and is never sold or shared
            externally.
          </p>
        </Section>

        <Section
          n="7"
          title="Cookies, local storage & analytics"
          icon={EyeSlashIcon}
          testId="sec-7"
        >
          <p>
            Bidorah uses a minimal set of cookies and browser storage to
            deliver the Service:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Strictly-necessary cookies</strong> for authentication and session security &mdash; these cannot be disabled without breaking Bidorah.</li>
            <li><strong>Functional storage</strong> (e.g. selected account type during signup, terms-acceptance flag) to carry context across pages.</li>
            <li><strong>Aggregate analytics</strong>: Bidorah tracks usage metrics to improve product quality. These are measured in aggregate and are not combined with third-party ad profiles.</li>
          </ul>
          <p>
            Bidorah does not participate in cross-site behavioural advertising.
            If you enable &ldquo;Do Not Track&rdquo; in your browser, Bidorah
            treats that as a signal to avoid optional analytics instrumentation.
          </p>
        </Section>

        <Section n="8" title="Sharing & disclosure" icon={GlobeIcon} testId="sec-8">
          <p>Bidorah shares personal information only in these narrow cases:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Service providers</strong> acting on Bidorah&rsquo;s behalf: Stripe, PayPal, LiveKit, object-storage providers, email deliverability vendors &mdash; each contractually bound to use the data only to deliver their service to Bidorah.</li>
            <li><strong>Other Bidorah users</strong>, limited to the public profile, listings, stream content, and chat you intentionally post.</li>
            <li><strong>Legal and safety</strong> disclosures when Bidorah believes in good faith that disclosure is required to comply with law, enforce Bidorah&rsquo;s Terms, or protect Bidorah, Bidorah users, or the public.</li>
            <li><strong>Business transitions</strong>: if Bidorah is involved in a merger, acquisition, or asset sale, user information may transfer as part of that transaction, with notice to affected users.</li>
          </ul>
        </Section>

        <Section n="9" title="Security & retention" icon={LockKeyIcon} testId="sec-9">
          <p>
            Bidorah applies industry-standard protections: TLS in transit,
            encrypted storage at rest for sensitive fields, bcrypt-hashed
            passwords with per-user salts, strict market segregation middleware
            on every wholesale endpoint, and continuous monitoring. No system
            is perfectly secure; Bidorah will notify affected users if a
            breach materially compromises their personal data, as required by
            law.
          </p>
          <p>
            Bidorah retains personal information only as long as needed to
            deliver the Service, satisfy legal/accounting obligations, or
            resolve disputes. When you delete your Bidorah account, Bidorah
            removes or anonymises personal identifiers within 90 days, except
            where retention is required by law (e.g. tax records, fraud
            investigations).
          </p>
        </Section>

        <Section n="10" title="Your rights & choices" icon={ShieldCheckIcon} testId="sec-10">
          <p>
            Depending on your jurisdiction (including California, the EEA, and
            the UK), you may have the right to:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Access</strong> the personal information Bidorah holds about you.</li>
            <li><strong>Correct</strong> inaccurate or incomplete information.</li>
            <li><strong>Delete</strong> your account and associated personal information (subject to legal retention obligations).</li>
            <li><strong>Portability</strong> &mdash; receive an export of your data in a structured, machine-readable format.</li>
            <li><strong>Object</strong> to or <strong>restrict</strong> specific processing activities.</li>
            <li><strong>Withdraw consent</strong> you previously granted, e.g. marketing emails.</li>
            <li><strong>Complain</strong> to your local data-protection regulator.</li>
          </ul>
          <p>
            To exercise any of these rights, email{" "}
            <a
              href="mailto:privacy@bidorah.com"
              className="text-[#1D78AB] font-semibold hover:underline"
            >
              privacy@bidorah.com
            </a>
            . Bidorah will respond within the timeframes required by applicable
            law (typically 30 days).
          </p>
        </Section>

        <Section n="11" title="Children's privacy" icon={UsersIcon} testId="sec-11">
          <p>
            Bidorah is not intended for anyone under 18. Bidorah does not
            knowingly collect personal information from minors. If Bidorah
            learns it has inadvertently collected data from a user under 18,
            Bidorah will promptly delete it. Parents or guardians who believe
            a minor has registered on Bidorah may contact{" "}
            <a
              href="mailto:privacy@bidorah.com"
              className="text-[#1D78AB] font-semibold hover:underline"
            >
              privacy@bidorah.com
            </a>
            .
          </p>
        </Section>

        <Section n="12" title="International users" icon={GlobeIcon} testId="sec-12">
          <p>
            Bidorah is operated from the United States. If you access Bidorah
            from outside the US, your information will be transferred to,
            stored, and processed in the US, which may have data-protection
            rules different from those in your country. Where required,
            Bidorah implements contractual safeguards (such as Standard
            Contractual Clauses) to protect cross-border transfers.
          </p>
        </Section>

        <Section n="13" title="Changes to this policy" icon={FileTextIcon} testId="sec-13">
          <p>
            Bidorah may update this Policy as the Service evolves. When
            Bidorah makes a material change, Bidorah will update the
            &ldquo;Last updated&rdquo; date, post the revised Policy on the
            Bidorah website, and &mdash; for changes that meaningfully affect
            your rights &mdash; notify you by email or in-product banner
            before the change takes effect.
          </p>
        </Section>

        <Section n="14" title="Contact Bidorah" icon={EnvelopeSimpleIcon} testId="sec-14">
          <p>
            Questions, requests, or concerns about Bidorah&rsquo;s privacy
            practices? Reach the Bidorah privacy team at{" "}
            <a
              href="mailto:privacy@bidorah.com"
              className="text-[#1D78AB] font-semibold hover:underline"
            >
              privacy@bidorah.com
            </a>
            . For legal or compliance inquiries, contact{" "}
            <a
              href="mailto:legal@bidorah.com"
              className="text-[#1D78AB] font-semibold hover:underline"
            >
              legal@bidorah.com
            </a>
            .
          </p>
          <p className="mt-8 text-xs text-zinc-500">
            © {new Date().getFullYear()} Bidorah. All rights reserved.
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
